import { useState } from 'react';

import Button from '@components/common/button';
import Select from '@components/common/select';
import Status from '@components/common/status';
import Textarea from '@components/common/textarea';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';

import { apiSandboxService, SandboxApiError } from '@api';
import type { SandboxExecResponse } from '@api';

import CodeBlock from '../_shared/CodeBlock';
import styles from './api_console.module.scss';

interface EndpointDef {
    id: string;
    label: string;
    method: string;
    path: string;
    example: Record<string, unknown>;
    write?: boolean;
}

const ENDPOINTS: EndpointDef[] = [
    {
        id: 'gift_cards.list',
        label: 'List gift cards',
        method: 'GET',
        path: '/v1/gift-cards',
        example: { limit: 10 }
    },
    {
        id: 'gift_cards.get',
        label: 'Get gift card',
        method: 'GET',
        path: '/v1/gift-cards/{id}',
        example: { id: 'gc_test_...' }
    },
    {
        id: 'payments.list',
        label: 'List payments',
        method: 'GET',
        path: '/v1/payments',
        example: { limit: 10 }
    },
    {
        id: 'gift_cards.create',
        label: 'Create gift cards',
        method: 'POST',
        path: '/v1/gift-cards',
        example: { gift_cards: [{ amount: 25, currency: 'CAD' }] },
        write: true
    }
    // connect.sessions.create is intentionally NOT exposed here: the real /v1 action
    // validates redirect_origin against the merchant's allowed_domains (the server-derived
    // portal origin isn't in it → always 400), and the sandbox-mint alternative returns a
    // different response shape than /v1 — misleading for an API explorer. The "Test checkout"
    // flow above already exercises connect-session minting. Tracked for a fast-follow (#175).
    // Only endpoints whose data model is environment-scoped are exposed here — the
    // sandbox guarantees test-mode only. connect.customers.list and
    // webhooks.events.list are intentionally excluded until merchant_customer_links
    // and merchant_webhook_deliveries gain a test/live column (they would otherwise
    // surface live rows in the sandbox).
];

const DEFAULT_ENDPOINT = ENDPOINTS[0];

type ConsolePhase = 'idle' | 'loading' | 'done' | 'error';

const ApiConsole = () => {
    const [endpointId, setEndpointId] = useState<string>(DEFAULT_ENDPOINT.id);
    const [paramsJson, setParamsJson] = useState<string>(
        JSON.stringify(DEFAULT_ENDPOINT.example, null, 2)
    );
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [phase, setPhase] = useState<ConsolePhase>('idle');
    const [result, setResult] = useState<SandboxExecResponse | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const selectedEndpoint =
        ENDPOINTS.find((ep) => ep.id === endpointId) ?? DEFAULT_ENDPOINT;

    const handleEndpointChange = (value: string | number) => {
        const id = String(value);
        setEndpointId(id);
        const ep = ENDPOINTS.find((e) => e.id === id);
        if (ep) {
            setParamsJson(JSON.stringify(ep.example, null, 2));
        }
        setJsonError(null);
        setPhase('idle');
        setResult(null);
        setErrorMessage(null);
    };

    const handleParamsChange = (value: string) => {
        setParamsJson(value);
        if (jsonError) setJsonError(null);
    };

    const handleSend = async () => {
        let params: unknown;
        try {
            params = JSON.parse(paramsJson);
        } catch {
            setJsonError('Invalid JSON — fix the params and try again.');
            return;
        }
        if (
            typeof params !== 'object' ||
            params === null ||
            Array.isArray(params)
        ) {
            setJsonError('Params must be a JSON object {}.');
            return;
        }
        setPhase('loading');
        setResult(null);
        setErrorMessage(null);
        try {
            const data = await apiSandboxService.exec({
                endpoint: endpointId,
                params: params as Record<string, unknown>
            });
            setResult(data);
            setPhase('done');
        } catch (err) {
            const apiErr = err instanceof SandboxApiError ? err : null;
            const code = apiErr?.code;
            let msg = apiErr?.message ?? 'Unexpected error — could not execute request.';
            if (code === 'SANDBOX_001') {
                msg = `${msg} Your account must be a business account to use the sandbox.`;
            } else if (code === 'SANDBOX_004') {
                msg = `${msg} You have hit the rate cap — wait a moment and try again.`;
            } else if (code === 'SANDBOX_005') {
                msg = `${msg} This endpoint is not available in the sandbox.`;
            }
            setErrorMessage(code ? `[${code}] ${msg}` : msg);
            setPhase('error');
        }
    };

    const handleReset = () => {
        setPhase('idle');
        setResult(null);
        setErrorMessage(null);
    };

    const isLoading = phase === 'loading';
    const showResult = phase === 'done' || phase === 'error';
    const is2xx = result !== null && result.status >= 200 && result.status < 300;

    const selectItems = ENDPOINTS.map((ep) => ({ value: ep.id, label: ep.label }));

    return (
        <div className={styles.console_section}>
            <div className={styles.section_header}>
                <Typography variant="h5">Try it</Typography>
                <Typography variant="body3" className={styles.section_hint}>
                    Runs against your test data — no real money, no secret needed.
                </Typography>
            </div>

            <div className={styles.card}>
                <div className={styles.card_header}>
                    <div className={styles.icon_circle}>
                        <Icon name="send" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography variant="body1" weight="semibold">
                            API console
                        </Typography>
                        <p className={styles.description}>
                            Pick an endpoint, adjust params, and hit Send
                        </p>
                    </div>
                </div>

                <div className={styles.form}>
                    <Select
                        label="Endpoint"
                        value={endpointId}
                        onChange={handleEndpointChange}
                        items={selectItems}
                        full
                        disabled={isLoading}
                    />

                    <div className={styles.method_row} aria-label="Selected endpoint path">
                        <span
                            className={[
                                styles.method_pill,
                                selectedEndpoint.write ? styles.method_pill_post : ''
                            ]
                                .filter(Boolean)
                                .join(' ')}>
                            {selectedEndpoint.method}
                        </span>
                        <code className={styles.path_text}>{selectedEndpoint.path}</code>
                    </div>

                    {selectedEndpoint.write && (
                        <p className={styles.write_note}>
                            Creates test data in your sandbox (no real money).
                        </p>
                    )}

                    <Textarea
                        label="Params (JSON)"
                        value={paramsJson}
                        onChange={handleParamsChange}
                        variant="large"
                        full
                        disabled={isLoading}
                        error={!!jsonError}
                        helperText={jsonError ?? undefined}
                    />
                </div>

                <div className={styles.card_actions}>
                    <Button
                        variant="gold"
                        onClick={() => {
                            void handleSend();
                        }}
                        isLoading={isLoading}
                        disabled={isLoading}>
                        Send
                    </Button>
                    {showResult && (
                        <button
                            className={styles.reset_btn}
                            onClick={handleReset}
                            type="button"
                            aria-label="Reset console">
                            <Icon name="reload" width={14} height={14} />
                            Reset
                        </button>
                    )}
                </div>
            </div>

            {showResult && (
                <div className={styles.result_card}>
                    {phase === 'done' && result !== null && (
                        <>
                            <div className={styles.result_header}>
                                <div className={styles.result_meta}>
                                    <Status variant={is2xx ? 'success' : 'error'} type="default">
                                        {result.status}
                                    </Status>
                                    <code className={styles.result_path}>
                                        {result.method} {result.path}
                                    </code>
                                </div>
                            </div>
                            <CodeBlock
                                code={JSON.stringify(result.response, null, 2)}
                                language="JSON"
                                json
                            />
                        </>
                    )}
                    {phase === 'error' && (
                        <div className={styles.error_panel}>
                            <Icon
                                name="error_outlined"
                                width={20}
                                height={20}
                                className={styles.icon_error}
                            />
                            <Typography variant="body4">
                                {errorMessage ?? 'Request failed.'}
                            </Typography>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ApiConsole;
